import { ratePriceAdjustmentIdZodSchema } from "@@/shared/acquire/accommodation-engine/rate-price-adjustment-id";
import { weekdaysOnOffZodObject } from "@@/shared/acquire/accommodation-engine/weekdays-on-off";
import { dateRangeZodObject } from "@@/shared/date-time/date-range";
import {
    isoAndUnixZodSchema,
    isoTimestampZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import { productIdZodSchema } from "@@/shared/products/product-id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import {
    requiredTranslatableZodSchema,
    translatableStringFactory,
} from "@@/shared/translations/translation";
import { generateId } from "@@/utilities/generate-id";
import { z } from "zod";

const editRatePriceAdjustmentCommandZodObject = z.object({
    _id: idZodSchema,
    _type: z.literal("EDIT_RATE_PRICE_ADJUSTMENT_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    ratePriceAdjustmentId: ratePriceAdjustmentIdZodSchema,
    productId: productIdZodSchema,
    providerId: providerIdZodSchema,
    title: requiredTranslatableZodSchema,
    /** Date range of the adjustment. Adjustment is only valid within the date range */
    dateRange: dateRangeZodObject,
    weekdaysOn: weekdaysOnOffZodObject,
    /**
     * List of dates when the adjustment is deactivated. \
     * dates in ISO 8601 format. \
     * start of day in "Europe/Stockholm" timezone. \
     * (later when we've got it, the dates should probably be in the same timezone as the provider)
     */
    inactiveDates: z.array(isoTimestampZodSchema),
    adjustment: z.union([
        z.object({
            type: z.literal("PERCENTAGE"),
            value: z
                .number()
                .int()
                .refine(
                    value => value !== 0,
                    translatableStringFactory({
                        sv: "Justeringen måste vara mer eller mindre än 0 kr",
                        en: "The adjustment must be more or less than 0 SEK",
                    }),
                ),
        }),
        z.object({
            type: z.literal("AMOUNT"),
            value: z.number(),
        }),
    ]),
});

type EditRatePriceAdjustmentCommand = z.infer<
    typeof editRatePriceAdjustmentCommandZodObject
>;

const editRatePriceAdjustmentCommandFactoryParamsZodObject =
    editRatePriceAdjustmentCommandZodObject.omit({
        _id: true,
        _type: true,
        _createdAt: true,
    });

type FactoryParams = z.infer<
    typeof editRatePriceAdjustmentCommandFactoryParamsZodObject
>;

const editRatePriceAdjustmentCommandFactory = (params: FactoryParams) => {
    const editCommand: EditRatePriceAdjustmentCommand = {
        ...params,
        _id: generateId({
            prefix: "command__",
        }),
        _type: "EDIT_RATE_PRICE_ADJUSTMENT_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return editCommand;
};

export {
    editRatePriceAdjustmentCommandFactory,
    editRatePriceAdjustmentCommandFactoryParamsZodObject,
    editRatePriceAdjustmentCommandZodObject,
};
export type {
    EditRatePriceAdjustmentCommand,
    FactoryParams as EditRatePriceAdjustmentCommandFactoryParams,
};
